import { useCallback, useEffect, useState } from "react";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import { adminService } from "../../features/admin/service";
import type { AnalyticsSummary } from "../../features/admin/types";
import RefreshButton from "../../components/admin/RefreshButton";

const AdminAnalyticsPage: React.FC = () => {
  const [data, setData] = useState<AnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    return adminService.getAnalytics(days).then(setData).catch(console.error).finally(() => setLoading(false));
  }, [days]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return <p className="text-gray-400">Failed to load analytics.</p>;

  const maxTrend = Math.max(...(data.daily_trend.map((t) => t.count) || [1]));
  const eventEntries = Object.entries(data.events_by_type).sort((a, b) => b[1] - a[1]);
  const maxEvent = eventEntries.length > 0 ? eventEntries[0][1] : 1;

  return (
    <div>
      <AdminPageHeader
        title="Analytics"
        subtitle="Platform event analytics"
        actions={
          <>
            <RefreshButton onRefresh={fetchAnalytics} />
            {[7, 14, 30, 60, 90].map((d) => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === d ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" : "bg-gray-900 text-gray-400 border border-gray-800 hover:bg-gray-800"
                }`}
              >
                {d}d
              </button>
            ))}
          </>
        }
      />

      {/* Total */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 mb-6">
        <p className="text-gray-500 text-sm">Total Events ({days} days)</p>
        <p className="text-4xl font-bold text-white mt-1">{data.total_events.toLocaleString()}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Event Trend */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Daily Event Trend</h2>
          {data.daily_trend.length === 0 ? (
            <p className="text-gray-500 text-sm">No data available.</p>
          ) : (
            <div className="flex items-end gap-1 h-52">
              {data.daily_trend.map((t) => (
                <div key={t.date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                  <div
                    className="w-full bg-cyan-500/30 hover:bg-cyan-500/50 rounded-t transition-all cursor-pointer min-h-[2px]"
                    style={{ height: `${(t.count / maxTrend) * 100}%` }}
                  />
                  <div className="absolute -top-8 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    {t.date}: {t.count}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Events by Type */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Events by Type</h2>
          {eventEntries.length === 0 ? (
            <p className="text-gray-500 text-sm">No events recorded.</p>
          ) : (
            <div className="space-y-3">
              {eventEntries.map(([type, count]) => (
                <div key={type}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-300">{type}</span>
                    <span className="text-gray-500">{count.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-gray-800 rounded-full h-2">
                    <div
                      className="h-2 bg-gradient-to-r from-indigo-500 to-cyan-500 rounded-full transition-all"
                      style={{ width: `${(count / maxEvent) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
